/**
 * Exact helpers for the decimal-string amounts of the payments runtime. Ledger balances carry up to 8
 * fraction digits and must not round-trip through `Number`/`parseFloat` — double precision can flip a
 * funds-sufficiency comparison right at the boundary. Comparisons scale both operands to `BigInt` instead;
 * display formatting goes through Intl directly (see `ColocaAmountDisplay`).
 */

const DECIMAL_STRING_RE = /^-?(\d+(\.\d+)?|\.\d+)$/;

/** Plain dot-decimal string, optional leading minus: "1500", "-0.25", ".5", "150000.00000000". */
export function isDecimalString(value: string): boolean {
  return DECIMAL_STRING_RE.test(value);
}

/** `value` scaled by 10^scale as an exact integer ("1.5" @ scale 3 → 1500n). Caller validates the input. */
function toScaledBigInt(value: string, scale: number): bigint {
  const negative = value.startsWith("-");
  const [intPart, fracPart = ""] = (negative ? value.slice(1) : value).split(".");
  const scaled = BigInt(intPart + fracPart.padEnd(scale, "0"));
  return negative ? -scaled : scaled;
}

/**
 * Exact numeric comparison of two decimal strings: -1 (a < b), 0 (equal), 1 (a > b). Both operands must
 * satisfy `isDecimalString` — validate before calling (BigInt throws on malformed digits).
 */
export function compareDecimalStrings(a: string, b: string): -1 | 0 | 1 {
  const scale = Math.max(a.split(".")[1]?.length ?? 0, b.split(".")[1]?.length ?? 0);
  const bigA = toScaledBigInt(a, scale);
  const bigB = toScaledBigInt(b, scale);
  if (bigA < bigB) return -1;
  if (bigA > bigB) return 1;
  return 0;
}

/**
 * The API returns money at 8 dp (`"816.32000000"`), which reads as noise inside an input the user types in —
 * especially for a 2-decimal currency. Trim the trailing zeros without touching the value: string-only, so no
 * `Number` round-trip and no precision lost. Display still goes through `ColocaAmountDisplay`.
 */
export function trimTrailingZeros(amount: string): string {
  if (!amount.includes(".")) return amount;
  return amount.replace(/0+$/, "").replace(/\.$/, "");
}

/* -------------------------------------------------------------------------------------------------
 * Fixed-point arithmetic at 8 dp — a faithful port of the runtime's `money.util.ts`.
 *
 * Any client-side figure that must equal what the backend will compute (the fee preview: fixed +
 * amount·percentage/100, clamped to [min,max]) has to use these, never `parseFloat`. A float preview that
 * disagrees with the ledger by 1e-8 makes the whole surface untrustworthy, and `0.1 + 0.2` already proves
 * the point. Only display formatting leaves the string domain (Intl, via `ColocaAmountDisplay`).
 * ------------------------------------------------------------------------------------------------- */

/** Fraction digits every runtime money value carries. Mirrors `SCALE` in the backend util. */
export const MONEY_SCALE = 8;

const MONEY_FACTOR = 10n ** BigInt(MONEY_SCALE);

/** Positive money as accepted by the runtime DTOs: ≤12 integer digits, ≤8 fraction digits. */
export const MONEY_INPUT_PATTERN = /^\d{1,12}(\.\d{1,8})?$/;

/** Percentage as accepted by `CreatePaymentFeeDto`: 0–100 with up to 4 fraction digits. */
export const PERCENTAGE_INPUT_PATTERN = /^(100(\.0{1,4})?|\d{1,2}(\.\d{1,4})?)$/;

function toMoneyScaled(value: string): bigint {
  const trimmed = value.trim();
  if (!isDecimalString(trimmed)) {
    throw new Error(`Invalid decimal string: "${value}"`);
  }
  const negative = trimmed.startsWith("-");
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const [intPart, fracPart = ""] = unsigned.split(".");
  if (fracPart.length > MONEY_SCALE) {
    throw new Error(`Decimal "${value}" exceeds ${MONEY_SCALE} fraction digits`);
  }
  const scaled = BigInt(intPart || "0") * MONEY_FACTOR + BigInt(fracPart.padEnd(MONEY_SCALE, "0"));
  return negative ? -scaled : scaled;
}

function fromMoneyScaled(scaled: bigint): string {
  const negative = scaled < 0n;
  const abs = negative ? -scaled : scaled;
  const frac = (abs % MONEY_FACTOR).toString().padStart(MONEY_SCALE, "0");
  return `${negative ? "-" : ""}${abs / MONEY_FACTOR}.${frac}`;
}

/** Canonical 8-dp form of a decimal string ("8" → "8.00000000"). Use to normalize before comparing. */
export function normalizeDecimal(value: string): string {
  return fromMoneyScaled(toMoneyScaled(value));
}

export function addDecimals(a: string, b: string): string {
  return fromMoneyScaled(toMoneyScaled(a) + toMoneyScaled(b));
}

export function subtractDecimals(a: string, b: string): string {
  return fromMoneyScaled(toMoneyScaled(a) - toMoneyScaled(b));
}

/**
 * `amount * percentage / 100`, exact at 8 dp with half-up rounding on the magnitude — the same rounding the
 * runtime applies, so a previewed fee matches the persisted one digit for digit.
 */
export function percentageOfDecimal(amount: string, percentage: string): string {
  const divisor = 100n * MONEY_FACTOR;
  const product = toMoneyScaled(amount) * toMoneyScaled(percentage);
  const negative = product < 0n;
  const magnitude = negative ? -product : product;
  const rounded = (magnitude + divisor / 2n) / divisor;
  return fromMoneyScaled(negative ? -rounded : rounded);
}

/** `a / b` at 8 dp — a port of the runtime's `divideMoney`, same rounding modes on the magnitude. */
export function divideDecimals(a: string, b: string, mode: "half-up" | "up" | "down" = "half-up"): string {
  const divisor = toMoneyScaled(b);
  if (divisor === 0n) throw new Error("Division by zero in money arithmetic");
  const dividend = toMoneyScaled(a) * MONEY_FACTOR;
  const negative = dividend < 0n !== divisor < 0n;
  const absDividend = dividend < 0n ? -dividend : dividend;
  const absDivisor = divisor < 0n ? -divisor : divisor;
  const rounded =
    mode === "half-up"
      ? (absDividend * 2n + absDivisor) / (absDivisor * 2n)
      : mode === "up"
        ? (absDividend + absDivisor - 1n) / absDivisor
        : absDividend / absDivisor;
  return fromMoneyScaled(negative ? -rounded : rounded);
}

/** Rounds UP to `decimals` places, as the runtime rounds every fee and tax it charges. */
export function roundUpDecimal(value: string, decimals: number): string {
  const scaled = toMoneyScaled(value);
  const step = 10n ** BigInt(MONEY_SCALE - decimals);
  const remainder = ((scaled % step) + step) % step;
  return fromMoneyScaled(remainder === 0n ? scaled : scaled - remainder + step);
}

/** True when the string parses as a decimal and is strictly greater than zero. */
export function isPositiveDecimal(value: string): boolean {
  return isDecimalString(value) && compareDecimalStrings(value, "0") > 0;
}
